/**
 * `/kebijakan-penyedia` — public, landing-styled (`.mk`) table of what every AI provider allows in
 * each way Bystep could use it (Orkestrator E3 §4). Data: GET /api/provider-policy (DB rows over the
 * JSON seed); the seed renders first, so the page never waits on the network.
 */
import { createFileRoute } from '@tanstack/react-router'
import { ExternalLink, ShieldCheck } from 'lucide-react'
import { Container, MkFooter, MkNav, Overline } from '#/components/landing/mk'
import { policyDate, useProviderPolicy } from '#/components/byoa/policy-badge'
import { OFFERED_SCENARIOS, POLICY_SCENARIOS, seedTable, type PolicyScenario, type PolicyStatus } from '#/lib/agents/policy'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/kebijakan-penyedia')({
  head: () => ({
    meta: [
      { title: `${m.pol_page_title()} · Bystep` },
      { name: 'description', content: m.pol_page_meta_desc() },
      { property: 'og:title', content: m.pol_page_title() },
      { property: 'og:description', content: m.pol_page_meta_desc() },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://bystep.dev/brand/bystep-og.png' },
    ],
  }),
  component: PolicyPage,
})

const SCENARIO_TITLE: Record<PolicyScenario, () => string> = { a: m.pol_scenario_a, b1: m.pol_scenario_b1, b2: m.pol_scenario_b2, c: m.pol_scenario_c, d: m.pol_scenario_d }
const SCENARIO_DESC: Record<PolicyScenario, () => string> = { a: m.pol_scenario_a_desc, b1: m.pol_scenario_b1_desc, b2: m.pol_scenario_b2_desc, c: m.pol_scenario_c_desc, d: m.pol_scenario_d_desc }
const STATUS_LABEL: Record<PolicyStatus, () => string> = { allowed: m.pol_status_allowed, gray: m.pol_status_gray, forbidden: m.pol_status_forbidden }
const STATUS_CLS: Record<PolicyStatus, string> = {
  allowed: 'bg-status-done/12 text-status-done border-status-done/30',
  gray: 'bg-muted text-muted-foreground border-border',
  forbidden: 'bg-status-failed/10 text-status-failed border-status-failed/30',
}

function StatusPill({ status }: { status: PolicyStatus }) {
  return <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[12px] font-semibold', STATUS_CLS[status])} data-status={status}>{STATUS_LABEL[status]()}</span>
}

function PolicyPage() {
  const { data } = useProviderPolicy()
  const table = data ?? seedTable()
  return (
    <div className="mk h-full overflow-y-auto overflow-x-hidden scroll-smooth">
      <MkNav />
      <main data-testid="provider-policy-page">
        <section className="py-12 lg:py-16">
          <Container className="flex flex-col gap-6">
            <Overline>{m.pol_link()}</Overline>
            <h1 className="max-w-[24ch]">{m.pol_page_title()}</h1>
            <p className="mk-lead max-w-[60ch]">{m.pol_page_lead()}</p>
            <div className="flex max-w-3xl gap-3 rounded-2xl border border-[var(--mk-border)] bg-[var(--mk-bg-muted)] p-5" data-testid="policy-promise">
              <ShieldCheck className="mt-0.5 size-5 shrink-0 text-status-done" aria-hidden />
              <div>
                <p className="font-semibold">{m.pol_no_token_promise()}</p>
                <p className="mt-1 text-[15px] text-[var(--mk-fg-muted)]">{m.pol_no_token_detail()}</p>
              </div>
            </div>
            <p className="text-sm text-[var(--mk-fg-muted)]" data-testid="policy-verified">{m.pol_verified_all({ date: policyDate(table.verifiedAt) })}</p>
          </Container>
        </section>

        <section className="py-6 lg:py-10">
          <Container className="flex flex-col gap-5">
            <h2>{m.pol_scenarios_title()}</h2>
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {POLICY_SCENARIOS.map((s) => (
                <li key={s} className="rounded-2xl border border-[var(--mk-border)] p-4" data-scenario={s}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-semibold">{SCENARIO_TITLE[s]()}</span>
                    <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', OFFERED_SCENARIOS.includes(s) ? 'bg-status-done/12 text-status-done' : 'bg-muted text-muted-foreground')}>{OFFERED_SCENARIOS.includes(s) ? m.pol_offered() : m.pol_not_offered()}</span>
                  </div>
                  <p className="mt-1 text-[14px] text-[var(--mk-fg-muted)]">{SCENARIO_DESC[s]()}</p>
                </li>
              ))}
            </ul>
          </Container>
        </section>

        <section className="py-6 lg:py-10">
          <Container className="flex flex-col gap-4">
            <h2>{m.pol_table_title()}</h2>
            <p className="text-sm text-[var(--mk-fg-muted)] lg:hidden">{m.pol_table_scroll()}</p>
            <div className="overflow-x-auto rounded-2xl border border-[var(--mk-border)]">
              <table className="w-full min-w-[880px] border-collapse text-left text-[13px]" data-testid="provider-policy-table">
                <thead>
                  <tr className="bg-[var(--mk-bg-muted)]">
                    <th scope="col" className="p-3 font-semibold">{m.pol_th_provider()}</th>
                    {POLICY_SCENARIOS.map((s) => <th key={s} scope="col" className="p-3 font-semibold">{SCENARIO_TITLE[s]()}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {table.providers.map((p) => (
                    <tr key={p.id} className="border-t border-[var(--mk-border)] align-top" data-provider={p.id}>
                      <th scope="row" className="p-3 font-semibold">{p.label}</th>
                      {POLICY_SCENARIOS.map((s) => {
                        const cell = p.scenarios[s]
                        return (
                          <td key={s} className="p-3" data-scenario={s} data-status={cell?.status ?? 'none'}>
                            {cell ? (
                              <div className="flex flex-col items-start gap-1.5">
                                <StatusPill status={cell.status} />
                                {cell.note ? <span className="text-[12px] leading-snug text-[var(--mk-fg-muted)]">{cell.note}</span> : null}
                                <span className="text-[11px] text-[var(--mk-fg-muted)]">{m.pol_verified({ date: policyDate(cell.verifiedAt) })}</span>
                                {cell.sourceUrl ? (
                                  <a href={cell.sourceUrl} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-[11px] underline underline-offset-2">
                                    {m.pol_source()} <ExternalLink className="size-3" aria-hidden />
                                  </a>
                                ) : null}
                              </div>
                            ) : '—'}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Container>
        </section>

        <section className="py-6 pb-20 lg:py-10">
          <Container className="flex flex-col gap-4">
            <h2>{m.pol_legend_title()}</h2>
            <dl className="grid max-w-3xl gap-3">
              {(['allowed', 'gray', 'forbidden'] as const).map((s) => (
                <div key={s} className="flex flex-wrap items-start gap-3">
                  <dt><StatusPill status={s} /></dt>
                  <dd className="text-[15px] text-[var(--mk-fg-muted)]">{s === 'allowed' ? m.pol_legend_allowed() : s === 'gray' ? m.pol_legend_gray() : m.pol_legend_forbidden()}</dd>
                </div>
              ))}
            </dl>
            <p className="max-w-3xl text-sm text-[var(--mk-fg-muted)]">{m.pol_data_note()}</p>
          </Container>
        </section>
      </main>
      <MkFooter />
    </div>
  )
}
